import React, { useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const overlay = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(8, 12, 20, 0.92)",
  zIndex: 2000,
  display: "flex",
  flexDirection: "column",
};

const navBtn = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: 46,
  height: 46,
  borderRadius: "50%",
  border: "1px solid rgba(255,255,255,0.25)",
  backgroundColor: "rgba(15,23,42,0.78)",
  color: "#f8fafc",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

const thumb = {
  width: 52,
  height: 52,
  objectFit: "cover",
  borderRadius: 8,
  border: "2px solid rgba(255,255,255,0.3)",
  cursor: "pointer",
  flexShrink: 0,
  background: "#0f172a",
};

/**
 * Full-screen product image viewer with forward / back navigation.
 * Navigation: on-screen arrows, ArrowLeft / ArrowRight keys, the thumbnail
 * strip, and Escape to close. Clicking the backdrop also closes.
 */
const ImageLightbox = ({ images = [], index = 0, onIndexChange, onClose, title }) => {
  const total = images.length;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && total) {
        onIndexChange((index + 1) % total);
      }
      if (e.key === "ArrowLeft" && total) {
        onIndexChange((index - 1 + total) % total);
      }
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [index, total, onClose, onIndexChange]);

  if (!total) return null;
  const current = images[index] || "";

  return (
    <div
      style={overlay}
      role="dialog"
      aria-modal="true"
      aria-label={title || "Product images"}
      onClick={onClose}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "14px 20px",
          color: "#e2e8f0",
          fontSize: 14,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <strong style={{ fontSize: 15 }}>{title}</strong>
          {total > 1 && (
            <span style={{ marginLeft: 10, color: "#94a3b8" }}>
              {index + 1} / {total}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close image viewer"
          style={{
            background: "rgba(15,23,42,0.78)",
            border: "1px solid rgba(255,255,255,0.25)",
            color: "#f8fafc",
            borderRadius: 8,
            padding: "6px 10px",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <X size={18} /> Close
        </button>
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
          padding: "0 64px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {total > 1 && (
          <button
            type="button"
            style={{ ...navBtn, left: 16 }}
            onClick={() => onIndexChange((index - 1 + total) % total)}
            aria-label="Previous image"
          >
            <ChevronLeft size={26} />
          </button>
        )}

        {current ? (
          <img
            src={current}
            alt={title || "Product"}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        ) : (
          <div style={{ color: "#94a3b8", fontSize: 15 }}>
            Image could not be loaded
          </div>
        )}

        {total > 1 && (
          <button
            type="button"
            style={{ ...navBtn, right: 16 }}
            onClick={() => onIndexChange((index + 1) % total)}
            aria-label="Next image"
          >
            <ChevronRight size={26} />
          </button>
        )}
      </div>

      {total > 1 && (
        <div
          style={{
            display: "flex",
            gap: 8,
            padding: 12,
            overflowX: "auto",
            justifyContent: "center",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((src, i) => (
            <img
              key={`${src}-${i}`}
              src={src}
              alt={`${title || "Product"} ${i + 1}`}
              onClick={() => onIndexChange(i)}
              style={
                i === index
                  ? { ...thumb, borderColor: "#f59e0b", transform: "scale(1.06)" }
                  : thumb
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;